/** A manifest step referenced into a Rain chat message as context (the
 *  "add to chat" affordance on `ManifestStepCard`). */
export interface IStepReference {
  number: number;
  title: string;
  fn?: string;
  /** The focused test/replay run's id, when the step was added to chat
   *  while a live run preview was focused — lets the agent tie the
   *  reference to that specific run's execution instead of just the
   *  step's static definition. Omitted in the plain authoring view. */
  runId?: string;
}

// number/fn/run_id are attributes (short, safe to quote); title is the tag's
// text content, not an attribute, since a jarvis-derived docstring title
// could contain a literal `"` that would break an attribute value.
const STEP_REFERENCE_RE =
  /^<step_reference\s+number="(\d+)"(?:\s+fn="([^"]*)")?(?:\s+run_id="([^"]*)")?\s*>([\s\S]*?)<\/step_reference>$/;
const LEADING_STEP_REFERENCE_RE =
  /^<step_reference\s+number="\d+"(?:\s+fn="[^"]*")?(?:\s+run_id="[^"]*")?\s*>[\s\S]*?<\/step_reference>\s*/;

/** Serializes a step reference into the `<step_reference>` tag embedded in
 *  an outgoing chat message. Deliberately not `<user_action>` — that tag is
 *  the backend's real instruction-execution protocol (see
 *  `sanitizeUserActionForDisplay`), and an unrecognized action type risks
 *  the agent trying to act on it rather than just reading it as context. */
export function formatStepReference(reference: IStepReference): string {
  const fnAttr = reference.fn ? ` fn="${reference.fn}"` : '';
  const runIdAttr = reference.runId ? ` run_id="${reference.runId}"` : '';
  return `<step_reference number="${reference.number}"${fnAttr}${runIdAttr}>${reference.title}</step_reference>`;
}

/** Parses a single `<step_reference>` tag back into its structured form.
 *  Returns null for anything else (plain text, a text-selection snippet,
 *  or a malformed tag) so callers can fall back to rendering it as-is. */
export function parseStepReference(text: string): IStepReference | null {
  const match = text.trim().match(STEP_REFERENCE_RE);
  if (!match) return null;
  const [, numberStr, fn, runId, title] = match;
  const number = Number(numberStr);
  if (!Number.isFinite(number)) return null;
  return {
    number,
    title: title.trim(),
    ...(fn ? { fn } : {}),
    ...(runId ? { runId } : {}),
  };
}

/** Strips every leading `<step_reference>` tag off a message body (a send
 *  can carry more than one, folded back-to-back by `foldSelectionSnippets`),
 *  returning the parsed references plus whatever text follows them. */
export function extractStepReferences(content: string): {
  references: IStepReference[];
  text: string;
} {
  const references: IStepReference[] = [];
  let rest = content;
  for (
    let match = rest.match(LEADING_STEP_REFERENCE_RE);
    match;
    match = rest.match(LEADING_STEP_REFERENCE_RE)
  ) {
    const reference = parseStepReference(match[0]);
    if (reference) references.push(reference);
    rest = rest.slice(match[0].length);
  }
  return { references, text: rest.trim() };
}
